/**
 * RP4 — Scene teardown helpers extracted from `Scene3DStage.tsx`.
 *
 * Pure dispose functions that walk the various per-bucket / per-entity
 * mesh registries the SceneContent component owns and free the
 * underlying GPU resources (geometry buffers, materials, textures,
 * points clouds).
 *
 * # Why a dedicated module
 *
 * The bulk-load effect originally ran an early-section dispose pass
 * before rebuilding meshes AND a late-section cleanup return that did
 * the same work on unmount. Hoisting the dispose primitives lets the
 * orchestrator call one function per registry rather than inlining
 * 4–6 distinct loops in two places. The helpers are framework-agnostic
 * (no React) — they take the THREE scene and the ref maps.
 *
 * # Opt 8 — Map consolidation (Phase 2)
 *
 * Pre-consolidation the helpers walked eight independent Maps; now
 * they iterate `Map<MeshKeyId, BucketState>` and
 * `Map<string, EntityRender>` and read individual sub-fields from
 * each entry. Atomic delete via `buckets.clear()` /
 * `entityRenders.clear()` replaces four-call sequences.
 */

import * as THREE from "three";

import { disposePointsCloud, type VisibleEntityGroups } from "../lod";
import { releaseLineSegmentsGeometry } from "./meshPool";

import type { BucketState, EntityRender } from "./SceneRefsTypes";
import type { MeshKeyId } from "../batchedLayer";

/**
 * Dispose a THREE object's material(s), tolerating an absent material (a text
 * object may carry `undefined`). Single truth for the `Array.isArray` material
 * fan-out every teardown block below otherwise inlined (jscpd one-truth).
 */
function disposeObjectMaterial(
  material: THREE.Material | THREE.Material[] | undefined,
): void {
  if (Array.isArray(material)) {
    for (const m of material) m.dispose();
  } else if (material) {
    material.dispose();
  }
}

/**
 * Remove every arrow decorator mesh (dimension arrow / tick heads or
 * multileader leader arrows) from the scene and free its per-mesh material.
 * The BufferGeometry is the module-cached arrow prototype singleton owned by
 * `getArrowPrototype` — NEVER disposed here (the next bulkLoad rebuilds an
 * InstancedMesh referencing it; disposing it would blank all arrows). Shared
 * by the pre-bulkLoad dispose and the unmount cleanup so the two paths cannot
 * drift.
 */
function disposeArrowDecoratorMeshes(
  scene: THREE.Scene,
  meshes: ReadonlyArray<THREE.InstancedMesh | THREE.LineSegments>,
): void {
  for (const am of meshes) {
    scene.remove(am);
    disposeObjectMaterial(am.material);
  }
}

/**
 * Dispose the batched-INSERT `InstancedMesh`es (`materializeInstancedBlockInsertMesh`
 * output) and clear the tracker array. Geometry is per-mesh here (unlike the
 * arrow prototypes) so it IS freed. Shared by the pre-bulkLoad dispose and the
 * unmount cleanup (jscpd one-truth).
 */
function disposeInstancedBlockMeshes(
  scene: THREE.Scene,
  meshes: THREE.InstancedMesh[],
): void {
  for (const mesh of meshes) {
    scene.remove(mesh);
    mesh.geometry.dispose();
    disposeObjectMaterial(mesh.material);
  }
  meshes.length = 0;
}

/**
 * Dispose the per-entity meshes a single `EntityRender` owns — the hatch /
 * wipeout fill mesh, the text / mtext object, and the dimension / multileader
 * arrow decorators. Shared by the pre-bulkLoad dispose (`disposeSceneBuckets`)
 * and the unmount cleanup (`disposeSceneOnUnmount`), which run the identical
 * per-render teardown (jscpd one-truth). Pattern / atlas textures and the
 * arrow prototype geometry are module-cached and intentionally NOT freed here.
 */
function disposeEntityRenderMeshes(
  scene: THREE.Scene,
  render: EntityRender,
): void {
  // Fill mesh (hatch / wipeout) — geometry is per-mesh, dispose it.
  if (render.fillMesh) {
    scene.remove(render.fillMesh);
    render.fillMesh.geometry.dispose();
    disposeObjectMaterial(render.fillMesh.material);
  }
  // Text / mtext object — atlas textures stay cached; only the per-mesh
  // geometry + material is owned here.
  if (render.textObject) {
    scene.remove(render.textObject);
    const m = render.textObject as THREE.Mesh | THREE.InstancedMesh;
    if (m.geometry) m.geometry.dispose();
    disposeObjectMaterial(m.material);
  }
  // Array-rect instances (Codex PR #784 round-10 P2) — one InstancedMesh per
  // resolved base geometry, registered on the ARRAY entity's own render entry
  // (previously one composite-key entry per mesh, disposed through `fillMesh`).
  // Geometry + material are built for the array wrapper (a fresh
  // `buildHatchFillMesh` / `buildWipeoutFillMesh` result or the placeholder
  // plane), NOT shared with the base entity's own mesh, so both are freed here —
  // identical ownership to the composite entries this replaced.
  if (render.arrayInstancedMeshes) {
    for (const m of render.arrayInstancedMeshes) {
      scene.remove(m);
      const im = m as THREE.Mesh;
      if (im.geometry) im.geometry.dispose();
      disposeObjectMaterial(im.material);
    }
    render.arrayInstancedMeshes.length = 0;
  }
  // Arrow decorators — geometry is the module-cached prototype (never freed);
  // clear the array so a reused render ref cannot append stale meshes.
  if (render.arrowDecoratorMeshes) {
    disposeArrowDecoratorMeshes(scene, render.arrowDecoratorMeshes);
    render.arrowDecoratorMeshes.length = 0;
  }
  // S9b (Codex P2 — "Hide synthetic labels when culling parents") — synthesised
  // DIMENSION / MULTILEADER label meshes ride on the parent render (they have no
  // independent EntityRender of their own), so they are OWNED and disposed here.
  // Same ownership as `textObject`: per-mesh geometry + material freed, atlas
  // textures stay module-cached. Clear the array so a reused render ref cannot
  // retain stale meshes.
  if (render.satelliteTextObjects) {
    for (const obj of render.satelliteTextObjects) {
      scene.remove(obj);
      const m = obj as THREE.Mesh | THREE.InstancedMesh;
      if (m.geometry) m.geometry.dispose();
      disposeObjectMaterial(m.material);
    }
    render.satelliteTextObjects.length = 0;
  }
}

/**
 * Bundle of every mutable Map the SceneContent component owns whose
 * lifecycle is tied to the bulk-load pass. Opt 8 — passes the two
 * consolidated Maps (`buckets`, `entityRenders`) instead of eight
 * parallel structures.
 */
export interface SceneRegistries {
  buckets: Map<MeshKeyId, BucketState>;
  entityRenders: Map<string, EntityRender>;
  /**
   * Phase 2 Wave 2 Task 4 — InstancedMeshes materialised by
   * `materializeInstancedBlockInsertMesh` for batched INSERTs (groups
   * of size >= INSTANCED_INSERT_MIN_COUNT). Disposed alongside buckets
   * on the next bulkLoad / unmount. Optional so legacy callers that
   * don't wire instanced INSERTs still type-check.
   */
  instancedBlockMeshes?: Array<THREE.InstancedMesh>;
}

/**
 * Pre-bulkLoad dispose pass. Walks every per-bucket and per-entity
 * registry, removes the THREE objects from the scene, and frees
 * geometry / material handles. The pattern texture cache is NOT
 * cleared here — it lives module-scope and survives bulkLoads so
 * re-paints reuse hot textures (cleared explicitly via
 * `clearPatternTextureCache` on hot reload only).
 */
export function disposeSceneBuckets(
  scene: THREE.Scene,
  registries: SceneRegistries,
): void {
  // Whole-scene replacement on every entity-feed change. PR-B does
  // not yet support partial re-use of bucket meshes — that lands
  // alongside the entity-patch path in a follow-up.
  for (const bucket of registries.buckets.values()) {
    scene.remove(bucket.mesh);
    // Phase 2 Wave 1 (Task A) — bucket meshes are pool-managed; route
    // the geometry back to `meshPool` instead of disposing directly so
    // the next bulkLoad can reuse the shell. `bucket.mesh` is typed
    // `LineSegments2` whose `.geometry` is always
    // `LineSegmentsGeometry` — release unconditionally.
    releaseLineSegmentsGeometry(bucket.mesh.geometry);
    bucket.material.dispose();
    // Q18.B: dispose previous selection state texture.
    bucket.selectionTexture.dispose();
  }
  registries.buckets.clear();

  for (const render of registries.entityRenders.values()) {
    disposeEntityRenderMeshes(scene, render);
  }
  // PR-I — drop stale bucket render handles before the new bulkLoad
  // populates them. Buckets are re-keyed on every bulkLoad so old
  // entries never apply correctly to fresh meshes.
  // Q18.B + PR-J — clearing entityRenders also drops textureSlot
  // lookups and per-entity LOD bucket state. Entity ids may persist
  // across bulkLoads (DRAWORDER preserved), but bounds / geometry can
  // change, so we discard the previous bucketing and let the next
  // camera-frame recompute classifications afresh.
  registries.entityRenders.clear();

  // Phase 2 Wave 2 Task 4 — dispose batched-INSERT InstancedMeshes.
  if (registries.instancedBlockMeshes) {
    disposeInstancedBlockMeshes(scene, registries.instancedBlockMeshes);
  }
}

/**
 * Unmount cleanup pass. Same registries, same dispose loops; called
 * from the bulkLoad effect's return function. Identical to
 * `disposeSceneBuckets` — kept as a separate symbol so the call sites
 * read clearly ("dispose before rebuild" vs "dispose on unmount").
 */
export function disposeSceneOnUnmount(
  scene: THREE.Scene,
  registries: SceneRegistries,
): void {
  for (const bucket of registries.buckets.values()) {
    scene.remove(bucket.mesh);
    // Phase 2 Wave 1 (Task A) — same pool routing as
    // `disposeSceneBuckets`. On unmount the pool is NOT cleared so
    // remounted scenes still benefit; explicit `clearPool()` is the
    // hot-reload escape hatch.
    releaseLineSegmentsGeometry(bucket.mesh.geometry);
    bucket.material.dispose();
    bucket.selectionTexture.dispose();
  }
  registries.buckets.clear();

  for (const render of registries.entityRenders.values()) {
    disposeEntityRenderMeshes(scene, render);
  }
  registries.entityRenders.clear();

  // Phase 2 Wave 2 Task 4 — dispose batched-INSERT InstancedMeshes on
  // unmount. Mirrors `disposeSceneBuckets`.
  if (registries.instancedBlockMeshes) {
    disposeInstancedBlockMeshes(scene, registries.instancedBlockMeshes);
  }
}

/**
 * Tear down the LOD points cloud (if any) and reset the per-frame
 * tracking state (bucket map, snapshot, group lists). Called both at
 * the start of a bulkLoad pass and from the unmount cleanup return.
 *
 * Codex PR #784 round-12 P2 — `proxyCloudsRef` holds the ATTRIBUTED viewport
 * point-proxy clouds (`scene/viewportPointProxies.ts`), which live at the scene
 * root alongside the main cloud and share its lifetime exactly: a bulk load
 * replaces the geometry every dot was resolved from, so leaving them attached
 * would paint stale dots for ids the new drawing does not have.
 */
export function disposeLodCloud(
  scene: THREE.Scene,
  ref: { current: THREE.Points | null },
  lodGroupsRef: { current: VisibleEntityGroups },
  prevCameraSnapshotRef: { current: unknown },
  proxyCloudsRef?: { current: THREE.Points[] },
): void {
  if (ref.current) {
    scene.remove(ref.current);
    disposePointsCloud(ref.current);
    ref.current = null;
  }
  if (proxyCloudsRef) {
    for (const cloud of proxyCloudsRef.current) {
      scene.remove(cloud);
      disposePointsCloud(cloud);
    }
    proxyCloudsRef.current = [];
  }
  prevCameraSnapshotRef.current = null;
  lodGroupsRef.current = { full: [], bboxLine: [], point: [], skip: [] };
}
